import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleById, articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { articleThumbnails } from '@/mocks/thumbnails';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = slug ? getArticleById(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24 pb-14 md:pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              您访问的文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), to: '/' },
    { label: t('breadcrumb.categories'), to: '/category/pingtai-jieshao' },
    { label: category?.name || article.categoryId, to: `/category/${article.categoryId}` },
    { label: article.title },
  ];

  const schemaData = {
    Article: {
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: {
        '@type': 'Organization',
        name: article.author,
      },
      url: `https://www.jinbaobo.com/article/${article.id}`,
    },
    BreadcrumbList: {
      itemListElement: breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: item.to ? `https://www.jinbaobo.com${item.to}` : undefined,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={schemaData.Article} />
      <SchemaOrg type="BreadcrumbList" data={schemaData.BreadcrumbList} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24 pb-14 md:pb-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-8">
              <div className="mb-6">
                {category && (
                  <Link
                    to={`/category/${category.id}`}
                    className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full hover:bg-accent-200 transition-colors mb-4 cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line text-xs"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line text-xs"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line text-xs"></i> {article.readTime}
                  </span>
                </div>
              </div>

              <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
                <img
                  src={articleThumbnails[article.id]}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {relatedArticles.length > 0 && (
                <div className="mt-14 pt-10 border-t border-background-200">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">
                    相关文章
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.id}`}
                        className="group cursor-pointer"
                      >
                        <div className="h-36 rounded-lg overflow-hidden mb-3">
                          <img
                            src={articleThumbnails[rel.id]}
                            alt={rel.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {rel.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-1">{rel.date}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-10 pt-8 border-t border-background-200 text-center">
                <Link
                  to={category ? `/category/${category.id}` : '/category/pingtai-jieshao'}
                  className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回列表
                </Link>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}